import {testIdProps} from '@github-ui/test-id-props'
import {FormControl, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useEffect, useRef, useState} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useDuplicateSavedViewMutation} from '../../hooks/use-duplicate-saved-view-mutation'
import styles from './SavedViewDialog.module.css'
import {SavedViewMutationErrorBanner} from './SavedViewMutationErrorBanner'
import type {SavedView} from './types'

interface DuplicateSavedViewDialogProps {
  view: SavedView
  owner: string
  repo: string
  onClose: () => void
  onSuccess?: (newView: SavedView) => void
}

export function DuplicateSavedViewDialog({view, owner, repo, onClose, onSuccess}: DuplicateSavedViewDialogProps) {
  const [name, setName] = useState(() => LABELS.views.copyNamePrefix(view.name))
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const {mutate: duplicateView, isPending, error: duplicateError} = useDuplicateSavedViewMutation({owner, repo})

  const trimmedName = name.trim()
  const isNameValid = trimmedName.length > 0
  const showValidationError = hasAttemptedSubmit && !isNameValid && !isPending

  const handleSubmit = useCallback(() => {
    setHasAttemptedSubmit(true)
    if (!isNameValid || isPending) return
    duplicateView(
      {view, name: trimmedName},
      {
        onSuccess: ({savedView}) => {
          onClose()
          onSuccess?.(savedView)
        },
      },
    )
  }, [duplicateView, isNameValid, isPending, onClose, onSuccess, trimmedName, view])

  const nameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    nameInputRef.current?.select()
  }, [])

  return (
    <Dialog
      title={LABELS.views.duplicateTitle}
      onClose={onClose}
      width="large"
      height="auto"
      renderBody={({children}) => <div className={styles.body}>{children}</div>}
      footerButtons={[
        {buttonType: 'default', content: BUTTON_LABELS.cancel, onClick: onClose},
        {
          buttonType: 'primary',
          content: LABELS.views.duplicateConfirmationButton,
          onClick: handleSubmit,
          loading: isPending,
        },
      ]}
    >
      <div {...testIdProps('duplicate-saved-view-dialog')}>
        <SavedViewMutationErrorBanner
          message={duplicateError ? LABELS.views.duplicateError : null}
          onRetry={handleSubmit}
          isRetrying={isPending}
          flush
        />
        <div className={styles.content}>
          <p>{LABELS.views.duplicateContent(view.name)}</p>
          <FormControl required>
            <FormControl.Label>{LABELS.views.duplicateNameLabel}</FormControl.Label>
            <TextInput
              ref={nameInputRef}
              block
              value={name}
              onChange={e => setName(e.currentTarget.value)}
              aria-invalid={showValidationError || undefined}
              validationStatus={showValidationError ? 'error' : undefined}
              {...testIdProps('duplicate-saved-view-name-input')}
            />
            {showValidationError && (
              <FormControl.Validation variant="error">{LABELS.views.duplicateBlankNameError}</FormControl.Validation>
            )}
          </FormControl>
        </div>
      </div>
    </Dialog>
  )
}
